import {
  AppBar,
  Box,
  Button,
  Step,
  StepLabel,
  Stepper,
  Toolbar,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserSelector } from "store/auth/selector";
import { AuthState as AuthType } from "store/auth/types";
import { AuthState } from "store/rootReducer";
import { getPersonalAndSmartGoalsListBySlpIdSelector } from "store/slpForm/selector";
import {
  UpdateHopesGoalsAndAmbitionsSuccessPayload,
  WhereToFromHereSuccessPayload,
} from "store/slpForm/types";
import { PERSONAL_SMART_GOALS_EMPTY_ERROR_MSG } from "utils/constants/Messages";
import { AppPages } from "utils/enums/app-pages";
import { AppRoutings } from "utils/enums/app-routings";
import { SLPFormStep } from "utils/enums/slp-form";
import {
  getPagePermissions,
  hideLoaderForBatchApiCall,
  showLoaderForBatchApiCall,
} from "utils/helper";
import {
  ISlpFormContainerDispatch,
  IUpdateHopesGoalsAndAmbitions,
  IWhereToFromHere,
} from "utils/interfaces/slp-form";
import { steps } from "utils/constants/slp";
import { SLP_FORM_SUBMITTED_SUCCESSFULLY } from "utils/constants/constant";
import HopesGoalsAndAmbitions from "./HopesGoalsAndAmbitions";
import WhereToFromHere from "./WhereToFromHere";
import PersonalAndSmartGoals from "./PersonalAndSmartGoals";

export type SLpFormProps = ISlpFormContainerDispatch;

const SLPForm: React.FC<SLpFormProps> = (props) => {
  const {
    hopesGoalsAndAmbitionsRequest,
    plaUserListRequest,
    updateHopesGoalsAndAmbitionsRequest,
    personalAndSmartGoalsRequest,
    personalAndSmartGoalsBySlpIdRequest,
    addPersonalAndSmartGoalsRequest,
    addEditGoalsAssessmentRequest,
    deletePersonalAndSmartGoalsRequest,
    getCurrentSLPGoalsPDFRequest,
    getCurrentCoursesForAddEnrolmentRequest,
    whereToFromHereRequest,
  } = props;

  const personalAndSmartGoalsListBySlpId = useSelector(
    getPersonalAndSmartGoalsListBySlpIdSelector
  );
  const user = useSelector<AuthState, AuthType["user"]>(getUserSelector);

  const location = useLocation<{
    slpId: number;
    studentId: number;
    partNumber: SLPFormStep;
  }>();
  const { slpId, studentId, partNumber } = location.state || {};
  const history = useHistory();
  const activePartNumber = partNumber;

  const handleNextStep = () => {
    history.replace(location.pathname, {
      ...location.state,
      partNumber: activePartNumber + 1,
    });
  };

  const onHopesGoalsAndAmbitionsFormSuccess = (
    response: UpdateHopesGoalsAndAmbitionsSuccessPayload
  ) => {
    if (response.success) {
      hideLoaderForBatchApiCall();
      handleNextStep();
    }
  };

  const onSLPFormSuccess = (response: WhereToFromHereSuccessPayload) => {
    if (response.success) {
      hideLoaderForBatchApiCall();
      history.push(AppRoutings.SLPDashboard);
      toast.success(SLP_FORM_SUBMITTED_SUCCESSFULLY);
    }
  };

  const handleNext = (
    data?: IUpdateHopesGoalsAndAmbitions | IWhereToFromHere | null
  ) => {
    if (activePartNumber === SLPFormStep.HOPES_GOALS_AMBITIONS) {
      if (!data) return;
      if (updateHopesGoalsAndAmbitionsRequest) {
        showLoaderForBatchApiCall();
        const payload = {
          values: data as IUpdateHopesGoalsAndAmbitions,
          callback: onHopesGoalsAndAmbitionsFormSuccess,
        };
        updateHopesGoalsAndAmbitionsRequest(payload);
      }
    } else if (activePartNumber === SLPFormStep.PERSONAL_SMART_GOALS) {
      const isAnyGoal = personalAndSmartGoalsListBySlpId?.some(
        (goal) => goal.slpId === slpId
      );
      if (isAnyGoal) {
        handleNextStep();
      } else {
        toast.error(PERSONAL_SMART_GOALS_EMPTY_ERROR_MSG);
      }
    } else {
      if (!data) return;
      if (whereToFromHereRequest) {
        showLoaderForBatchApiCall();
        const payload = {
          values: data as IWhereToFromHere,
          callback: onSLPFormSuccess,
        };
        whereToFromHereRequest(payload);
      }
    }
  };

  useEffect(() => {
    if (!slpId || !studentId || !partNumber) {
      history.replace(AppRoutings.SLPDashboard);
    }
  }, [slpId, studentId, partNumber, history]);

  useEffect(() => {
    const isViewAllowed = getPagePermissions(
      user?.permissions,
      AppPages.SLPDashboard,
      "student learning plan"
    )?.view;

    if (!isViewAllowed) {
      history.goBack();
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, []);

  return (
    <div>
      <Box className="content-header simple-header">
        <Typography variant="h2" className="heading">
          SLP Goals & Assessment
        </Typography>
        <Box sx={{ display: "flex", columnGap: "14px" }}>
          <Button
            variant="outlined"
            className="px-16"
            onClick={() => history.goBack()}
          >
            Back
          </Button>
        </Box>
      </Box>
      <AppBar position="static" className="student-header inline-header">
        {activePartNumber <= steps.length && (
          <Toolbar
            disableGutters
            sx={{ flexWrap: "wrap" }}
            className="header-container-bottom"
          >
            {activePartNumber === SLPFormStep.HOPES_GOALS_AMBITIONS && (
              <Typography variant="h3" className="header-subtitle">
                Part 1 - Hopes, Goals and Ambitions
              </Typography>
            )}
            {activePartNumber === SLPFormStep.PERSONAL_SMART_GOALS && (
              <Typography variant="h3" className="header-subtitle">
                Part 2 - Personal and Smart Goals
              </Typography>
            )}
            {activePartNumber === SLPFormStep.WHERE_TO_FROM_HERE && (
              <Typography variant="h3" className="header-subtitle">
                Part 3 - Where to from Here?
              </Typography>
            )}

            <Stepper activeStep={activePartNumber - 1}>
              {steps.map((step) => (
                <Step key={step}>
                  <StepLabel>{step}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Toolbar>
        )}
      </AppBar>
      <Box className="stepper-box">
        <Box className="stepper-inner-box">
          {activePartNumber === SLPFormStep.HOPES_GOALS_AMBITIONS && (
            <HopesGoalsAndAmbitions
              hopesGoalsAndAmbitionsRequest={hopesGoalsAndAmbitionsRequest}
              plaUserListRequest={plaUserListRequest}
              handleNext={handleNext}
            />
          )}
          {activePartNumber === SLPFormStep.PERSONAL_SMART_GOALS && (
            <PersonalAndSmartGoals
              personalAndSmartGoalsRequest={personalAndSmartGoalsRequest}
              personalAndSmartGoalsBySlpIdRequest={
                personalAndSmartGoalsBySlpIdRequest
              }
              addPersonalAndSmartGoalsRequest={addPersonalAndSmartGoalsRequest}
              addEditGoalsAssessmentRequest={addEditGoalsAssessmentRequest}
              deletePersonalAndSmartGoalsRequest={
                deletePersonalAndSmartGoalsRequest
              }
              getCurrentSLPGoalsPDFRequest={getCurrentSLPGoalsPDFRequest}
              handleNext={handleNext}
            />
          )}
          {activePartNumber === SLPFormStep.WHERE_TO_FROM_HERE && (
            <WhereToFromHere
              hopesGoalsAndAmbitionsRequest={hopesGoalsAndAmbitionsRequest}
              getCurrentCoursesForAddEnrolmentRequest={
                getCurrentCoursesForAddEnrolmentRequest
              }
              handleNext={handleNext}
            />
          )}
        </Box>
      </Box>
    </div>
  );
};

export default SLPForm;
